import { useDndContext } from "@dnd-kit/core";
import { classifyTransfer } from "./classifyTransfer";
import { asDraggableData } from "./ids";
import type { TransferDestination } from "./types";

/**
 * Uniform drop-target highlight state for in-app transfer surfaces.
 *
 * Centralizes the rule every drop target should follow so highlighting reads
 * consistently across the rail and detail pages:
 *  - **valid** drop (a real move/copy/place/favorite/etc.) → active ring
 *    (`isOver: true`),
 *  - **invalid** drop (wrong kind for this surface) → the invalid ring
 *    (`disallowed: true`) so "you can't drop here" is unmistakable,
 *  - **no-op** ("already here") → no ring at all; the drag pill says so.
 *
 * Only fires for the target the pointer is actually over (`over.id === dropId`),
 * so a target never lights up just because a drag is in flight somewhere else.
 */
export function useTransferDropHighlight(
  dropId: string,
  destination: TransferDestination,
): { isOver: boolean; disallowed: boolean } {
  const { active, over } = useDndContext();

  if (!active || over?.id !== dropId) {
    return { isOver: false, disallowed: false };
  }

  const draggable = asDraggableData(active.data.current);
  if (!draggable) return { isOver: false, disallowed: false };

  const classification = classifyTransfer(draggable, destination);
  if (classification.kind === "invalid") {
    return { isOver: true, disallowed: true };
  }
  if (classification.kind === "noop") {
    return { isOver: false, disallowed: false };
  }
  return { isOver: true, disallowed: false };
}
